import { useMemo, useState } from 'react';
import { Box, CircularProgress, Alert, Typography, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';
import { CalendarDays, ShieldPlus, BarChart3, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePatientAuth } from '../../contexts/PatientAuthContext';
import { apiErrorMessage } from '../../lib/errorMapping';
import {
  AuthScreenLayout, AuthEmailPassword, AuthSubmitButton, OrDivider,
  alertSx, ACCENT_BLUE, type Feature,
} from '../auth/authShared';

// Icons are static; the copy is read from the `auth` namespace at render time so the panel
// re-renders in the new language the moment the switcher on this card is used.
const FEATURE_KEYS = [
  { icon: CalendarDays,  key: 'upcomingAppointments' },
  { icon: ShieldPlus,    key: 'medicalRecords' },
  { icon: BarChart3,     key: 'prescriptions' },
  { icon: MessageSquare, key: 'messages' },
] as const;

export function PatientLogin() {
  const { t } = useTranslation('auth');
  const { signIn } = usePatientAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const features: Feature[] = useMemo(
    () => FEATURE_KEYS.map(({ icon, key }) => ({
      icon,
      title: t(`feature.${key}`),
      desc: t(`feature.${key}Desc`),
    })),
    [t],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError(t('error.enterBoth'));
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await signIn(email, password);
      // PatientAuthContext takes over routing on success.
    } catch (err) {
      // The backend replies in English. apiErrorMessage runs it through the shared mapping so a
      // patient reading Telugu gets a Telugu message rather than a raw server string.
      setError(apiErrorMessage(err, t('error.invalidCredentials')));
      setLoading(false);
    }
  };

  return (
    <AuthScreenLayout
      heading={{ pre: t('patient.headingPre'), accent: t('patient.headingAccent') }}
      subtext={t('patient.subtext')}
      features={features}
      cardTitle={t('patient.portalTitle')}
      cardSubtitle={t('patient.title')}
    >
      <form onSubmit={handleSubmit}>
        {error && <Alert severity="error" sx={alertSx}>{error}</Alert>}

        <AuthEmailPassword
          email={email}
          password={password}
          onEmail={setEmail}
          onPassword={setPassword}
          disabled={loading}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Box component={Link} to="/forgot-password" sx={{ color: ACCENT_BLUE, fontWeight: 600, textDecoration: 'none', fontSize: '0.88rem' }}>
            {t('action.forgotPassword')}
          </Box>
        </Box>

        <AuthSubmitButton disabled={loading}>
          {loading ? (
            <><CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />{t('action.signingIn')}</>
          ) : (
            t('action.signIn')
          )}
        </AuthSubmitButton>
      </form>

      <OrDivider />

      {/* Secondary entry point kept small so it doesn't compete with the primary flow. */}
      <Typography sx={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.78rem' }}>
        {t('patient.areYouStaff')}{' '}
        <MuiLink component={Link} to="/login" sx={{ color: '#94a3b8', textDecoration: 'underline', fontWeight: 600 }}>
          {t('patient.staffSignIn')}
        </MuiLink>
      </Typography>
    </AuthScreenLayout>
  );
}
